import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { useMemo, useCallback } from "react";
import { isValidDecimalInput } from "@/utils/inputParse";

interface BasePriceTableProps {
  columns: Array<string>;
  data: Array<Record<string, string | number>>;
  isEditing: boolean;
  editData: Array<Record<string, string | number>>;
  setEditData: React.Dispatch<
    React.SetStateAction<Array<Record<string, string | number>>>
  >;
}

const BasePriceTable = ({
  columns,
  data,
  isEditing,
  editData,
  setEditData,
}: BasePriceTableProps) => {
  const columnStats = useMemo(() => columns.reduce((acc, column) => {
    const values = data
      .map((row) => Number(row[column]))
      .filter((val) => !isNaN(val) && val > 0);

    if (values.length > 0) {
      acc[column] = {
        max: Math.max(...values),
        min: Math.min(...values),
      };
    }
    return acc;
  }, {} as Record<string, { max: number; min: number }>), [columns, data]);

  const getCellBackgroundColor = useCallback((column: string, value: string | number) => {
    const numValue = Number(value);
    if (!value || numValue === 0 || isNaN(numValue)) {
      return "#999999";
    }

    const stats = columnStats[column];
    if (!stats) return "white";

    return "white";
  }, [columnStats]);

  const handleCellChange = useCallback((rowIndex: number, column: string, value: string) => {
    setEditData((prev) => {
      const newData = [...prev];
      newData[rowIndex] = {
        ...newData[rowIndex],
        [column]: value,
      };
      return newData;
    });
  }, [setEditData]);

  return (
    <Box
      sx={{
        width: "100%",
        mt: 2,
        border: "1px solid #e0e0e0",
        borderRadius: 1,
      }}
    >
      <TableContainer
        component={Paper}
        sx={{
          maxHeight: "70vh",
          overflow: "auto",
          position: "relative",
          width: "100%",
          minWidth: "100%",
          backgroundColor: "#f3f3f3ff",
        }}
      >
        <Table stickyHeader sx={{ tableLayout: "fixed", width: "auto" }}>
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  position: "sticky",
                  left: 0,
                  zIndex: 102,
                  width: 300,
                  minWidth: 300,
                  maxWidth: 300,
                  textAlign: "center",
                  borderRight: "2px solid #e0e0e0",
                }}
              >
                Supplier
              </TableCell>

              {columns.map((column) => (
                <TableCell
                  key={column}
                  sx={{
                    textAlign: "right",
                    width: 150,
                    maxWidth: 150,
                    minWidth: 150,
                    whiteSpace: "normal",
                    wordWrap: "break-word",
                    overflow: "visible",
                    borderRight: "1px solid #e0e0e0",
                    padding: "8px",
                  }}
                >
                  {column}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {(data &&
              data.length > 0 &&
              data.map((row, rowIndex) => (
                <TableRow key={rowIndex} hover>
                  <TableCell
                    sx={{
                      position: "sticky",
                      left: 0,
                      zIndex: 101,
                      width: 300,
                      minWidth: 300,
                      maxWidth: 300,
                      borderRight: "2px solid #e0e0e0",
                      backgroundColor: "#fff",
                      fontWeight: "bold",
                      fontSize: "0.9rem",
                    }}
                  >
                    {row.supplier_name}
                  </TableCell>
                  {!isEditing
                    ? columns.map((column, columnIndex) => (
                        <TableCell
                          key={columnIndex}
                          sx={{
                            textAlign: "right",
                            borderRight: "1px solid #e0e0e0",
                            width: 150,
                            minWidth: 150,
                            maxWidth: 150,
                            backgroundColor: getCellBackgroundColor(
                              column,
                              row[column]
                            ),
                          }}
                        >
                          {row[column] || ""}
                        </TableCell>
                      ))
                    : columns.map((column, columnIndex) => (
                        <TableCell
                          key={columnIndex}
                          sx={{
                            textAlign: "right",
                            borderRight: "1px solid #e0e0e0",
                            width: 150,
                            minWidth: 150,
                            maxWidth: 150,
                            backgroundColor: getCellBackgroundColor(
                              column,
                              editData?.[rowIndex]?.[column] ?? ""
                            ),
                          }}
                        >
                          <input
                            type="text"
                            value={editData?.[rowIndex]?.[column] || ""}
                            onChange={(e) => {
                              const value = e.target.value;

                              if (isValidDecimalInput(value, 4)) {
                                handleCellChange(rowIndex, column, value);
                              }
                            }}
                            style={{
                              width: "100%",
                              textAlign: "right",
                              padding: "6px 10px",
                              border: "1px solid rgba(0, 0, 0, 0.23)",
                              borderRadius: "4px",
                              fontSize: "0.875rem",
                              fontFamily: '"Roboto","Helvetica","Arial",sans-serif',
                              backgroundColor:
                                getCellBackgroundColor(column, editData?.[rowIndex]?.[column] ?? "") === "#999999"
                                  ? "#999999"
                                  : editData?.[rowIndex]?.[column] === row[column]
                                  ? "white"
                                  : "#fff9c4",
                            }}
                          />
                        </TableCell>
                      ))}
                </TableRow>
              ))) || (
              <TableRow>
                <TableCell
                  colSpan={columns.length + 1}
                  sx={{
                    fontStyle: "italic",
                    textAlign: "center",
                    backgroundColor: "white",
                    py: 5,
                  }}
                >
                  No data available
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default BasePriceTable;
